import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import {
  Flow,
  FlowInput,
  AutomationRun,
  FlowValidation
} from '../../types/models/whatsapp';

const FLOWS = 'whatsapp_flows';

/**
 * ConvoBot flows: live reads from Firestore, writes through the API so the
 * graph is validated server-side before it can be published.
 */
class FlowService {
  private map(id: string, data: any): Flow {
    return {
      id,
      name: data.name || '',
      description: data.description || '',
      status: data.status === 'published' ? 'published' : 'draft',
      version: data.version || 0,
      nodes: Array.isArray(data.nodes) ? data.nodes : [],
      edges: Array.isArray(data.edges) ? data.edges : [],
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt || 0,
      updatedAt: data.updatedAt || 0,
      publishedAt: data.publishedAt ?? null
    };
  }

  subscribe(
    onChange: (flows: Flow[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const q = query(collection(db, FLOWS), orderBy('updatedAt', 'desc'));

    return onSnapshot(
      q,
      (snapshot) => onChange(snapshot.docs.map((d) => this.map(d.id, d.data()))),
      (error) => {
        console.error('❌ Flow subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  async get(id: string): Promise<Flow> {
    const response = await apiClient.get<{ flow: Flow }>(`/flows/${id}`);
    return response.flow;
  }

  async create(input: { name: string; description?: string }): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>('/flows', input);
  }

  async save(id: string, input: Partial<FlowInput>): Promise<void> {
    await apiClient.patch(`/flows/${id}`, input);
  }

  /** Structural check without publishing. */
  async validate(input: FlowInput): Promise<FlowValidation> {
    return apiClient.post<FlowValidation>('/flows/validate', input);
  }

  /**
   * Publishes as a new version. Any other published flow is demoted to draft —
   * only one may be live, so which flow answers a message is unambiguous.
   */
  async publish(id: string): Promise<{ version: number; unpublished: number }> {
    return apiClient.post<{ version: number; unpublished: number }>(
      `/flows/${id}/publish`
    );
  }

  async unpublish(id: string): Promise<void> {
    await apiClient.post(`/flows/${id}/unpublish`);
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/flows/${id}`);
  }

  /** Run log — why a contact did or did not get a reply. */
  async listRuns(options: { flowId?: string; status?: string } = {}): Promise<
    AutomationRun[]
  > {
    const params = new URLSearchParams();
    if (options.flowId) params.set('flowId', options.flowId);
    if (options.status) params.set('status', options.status);

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<{ runs: AutomationRun[] }>(
      `/flow-runs${suffix}`
    );
    return response.runs;
  }
}

export const flowService = new FlowService();
export default flowService;
